import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../../utils/googleMaps';
import { MapPin, Clock, Truck, Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function FloristsParramatta() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        
        if (mapRef.current && window.google) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: -33.8175, lng: 151.0000 }, // Parramatta coordinates
            zoom: 14,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });

          // Add marker for Parramatta
          new google.maps.Marker({
            position: { lat: -33.8175, lng: 151.0000 },
            map: map,
            title: "Parramatta",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "brand-primary",
              fillOpacity: 0.8,
              strokeColor: "#fff",
              strokeWeight: 2
            }
          });
        }
      } catch (error) {
        console.error('Error loading map:', error);
        setMapError(true);
      }
    };

    initMap();
  }, []);

  const parramattaSearchUrl = "/search?location=Parramatta+NSW+2150%2C+Australia&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8175&lng=151.0000";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do Parramatta florists offer same-day delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, many Parramatta-based florists offer same-day delivery when orders are placed before 2pm on weekdays."
        }
      },
      {
        "@type": "Question",
        "name": "How much does flower delivery in Parramatta cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Delivery costs typically range from $6 to $15, depending on distance, florist, and delivery time."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do Parramatta florists deliver to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most florists in Parramatta deliver throughout Western Sydney including Westmead, Harris Park, Granville, and Rosehill."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Florists Parramatta | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Browse the top florists in Parramatta NSW offering same-day delivery. From corporate arrangements to family celebrations, MarketBloom connects you with Western Sydney's finest florists." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Local Guides</span>
              <span>•</span>
              <span>15 January 2025</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Florists in Parramatta for Same-Day Delivery 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/best-florists-parramatta-same-day-hero.jpg" 
              alt="Professional floral display at a Parramatta florist in the CBD business district"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding authentic local florists in Parramatta NSW who deliver same-day shouldn't mean settling for overpriced corporate chains or generic supermarket bouquets.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent Parramatta flower shops - the skilled artisans located between Church Street's business district and Westfield Parramatta's retail hub who craft professional arrangements that serve Western Sydney's diverse community.
            </p>
            <p className="leading-7 text-black">
              Whether you need urgent flower delivery to Westmead Hospital, corporate arrangements for a Church Street office, or celebration flowers delivered to a Harris Park family home, our network of local florists ensures fresh, beautiful blooms arrive on time - often within hours of ordering.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Parramatta's finest florists offering same-day delivery options</p>
            <Link 
              to={`${parramattaSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=parramatta-florists`}
              className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Parramatta Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Parramatta Florists Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">What Makes Parramatta Florists Special</h2>
            
            <p className="leading-7 text-black mb-6">
              Parramatta's independent florists are as diverse as Western Sydney itself. From the established flower shops near Parramatta Station to the boutique studios scattered throughout the CBD, these local artisans understand what Western Sydney residents need - professional corporate arrangements, multicultural celebration flowers, and designs that reflect our region's dynamic growth.
            </p>
            <p className="leading-7 text-black mb-6">
              Unlike generic flower delivery services that ship from distant warehouses, Parramatta florists source from Sydney Flower Market daily, ensuring peak freshness. They know that a bouquet delivered to a Westfield office needs to impress clients, and arrangements for Riverside Theatres events should be as sophisticated as the performances.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for professional arrangements" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Professional Excellence</h3>
                <p className="text-black">From corporate to cultural celebrations</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for Western Sydney knowledge" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Western Sydney Expertise</h3>
                <p className="text-black">Know every street from CBD to suburbs</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for same-day service" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Same-Day Service</h3>
                <p className="text-black">Available from multiple local florists</p>
              </div>
            </div>
          </div>

          {/* Same-Day Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Delivery Coverage</h2>
            
            <p className="leading-7 text-black mb-6">
              Many of Parramatta's best florists offer same-day delivery with varying cutoff times throughout the day. This service covers not just Parramatta (2150) but often extends to neighboring Western Sydney suburbs.
            </p>

            <div className="mb-6">
              <div 
                ref={mapRef} 
                className="w-full h-80 rounded-lg shadow-md bg-gray-200"
              >
                {mapError && (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <MapPin className="w-8 h-8 mr-2" />
                    <span>Map unavailable - Parramatta NSW 2150</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Parramatta</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Westmead</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Harris Park</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Granville</span>
              </div>
            </div>
          </div>

          {/* Popular Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Popular Flower Styles in Parramatta</h2>
            
            <p className="leading-7 text-black mb-6">
              The best Parramatta florists understand their diverse clientele perfectly. Corporate professionals need sophisticated arrangements that impress in boardrooms and reception areas. Multicultural families celebrate with flowers that honor their heritage - from vibrant Indian marigolds to elegant European roses. And the growing young professional community seeks modern designs that complement contemporary apartments.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-4">Most Requested Styles:</h3>
              <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                <li className="text-black">Corporate arrangements with sophisticated color palettes</li>
                <li className="text-black">Multicultural celebration flowers honoring diverse traditions</li>
                <li className="text-black">Modern minimalist designs for young professionals</li>
                <li className="text-black">Traditional arrangements with premium roses and lilies</li>
              </ul>
            </div>
          </div>

          {/* Why Choose MarketBloom for Parramatta Flower Delivery */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Local Parramatta Florists</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Supporting Western Sydney Small Businesses</h3>
                  <p className="leading-7 text-black">When you order through MarketBloom, 100% of your flower purchase goes directly to independent Parramatta florists. Unlike corporate chains that take hefty commissions, we believe in empowering local artisans who know every corner from Church Street to Westfield Parramatta.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Real-Time Availability from Multiple Florists</h3>
                  <p className="leading-7 text-black">Our platform connects you with several Parramatta flower shops simultaneously, showing live inventory and same-day delivery slots. This means better selection for last-minute orders and competitive pricing from florists competing for your business.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Transparent Reviews from Real Parramatta Customers</h3>
                  <p className="leading-7 text-black">Every florist review on MarketBloom comes from verified local deliveries. Read honest feedback from your Parramatta neighbours about flower quality, delivery timing, and customer service before choosing your florist.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Efficient Western Sydney Delivery Network</h3>
                  <p className="leading-7 text-black">Shorter delivery distances mean fresher flowers and faster service. Our Parramatta florists understand the unique logistics of Western Sydney, from CBD offices to suburban homes, ensuring your bouquet arrives in perfect condition.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Celebrating Parramatta's Multicultural Heritage</h3>
                  <p className="leading-7 text-black">By choosing local florists over mass-market alternatives, you're supporting businesses that understand and celebrate Western Sydney's rich cultural diversity. These artisan florists create arrangements that honor traditions from around the world.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Linking Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Explore Florists in Other Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Looking for flowers elsewhere in Sydney? MarketBloom connects you with the best local florists across the city:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/search?location=Newtown+NSW+2042&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8978149&lng=151.1785003&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Newtown florists - bohemian blooms and creative arrangements
              </Link>
              <Link to="/search?location=Bondi+NSW+2026&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8915&lng=151.2767&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Bondi florists - beachside blooms and coastal arrangements
              </Link>
              <Link to="/search?location=Surry+Hills+NSW+2010&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8886109&lng=151.2094393&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Surry Hills florist listings - trendy inner-city options
              </Link>
              <Link to="/search?location=Marrickville+NSW+2204&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.9113438&lng=151.1553063&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Marrickville florists - multicultural floral artistry
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg px-6 py-5 mb-8 shadow-sm">
            <blockquote className="italic text-black">
              <p className="leading-7 mb-2">
                "MarketBloom helped me send corporate flowers to our Church Street office within 3 hours — the professional arrangement was perfect for our client meeting!"
              </p>
              <footer className="text-sm font-medium not-italic">
                – Michael T., Parramatta
              </footer>
            </blockquote>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 0}
                  aria-controls="faq-answer-0"
                >
                  <h3 className="text-xl font-semibold text-black">Do Parramatta florists offer same-day delivery?</h3>
                  {openFaq === 0 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-0" className={openFaq === 0 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Yes, many Parramatta-based florists offer same-day delivery when orders are placed before 2pm on weekdays.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <h3 className="text-xl font-semibold text-black">How much does flower delivery in Parramatta cost?</h3>
                  {openFaq === 1 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-1" className={openFaq === 1 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Delivery costs typically range from $6 to $15, depending on distance, florist, and delivery time.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <h3 className="text-xl font-semibold text-black">What areas do Parramatta florists deliver to?</h3>
                  {openFaq === 2 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-2" className={openFaq === 2 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Most florists in Parramatta deliver throughout Western Sydney including Westmead, Harris Park, Granville, and Rosehill.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-[brand-primary] to-[#E63E1C] text-white rounded-lg p-8 text-center md:relative sticky bottom-0 z-50">
            <h2 className="scroll-m-20 text-3xl font-semibold tracking-tight text-white mb-4">Ready to Order Beautiful Flowers?</h2>
            <p className="text-xl leading-7 text-white mb-6 opacity-90">
              Browse Parramatta's premier florists offering same-day delivery on professional arrangements
            </p>
            <Link 
              to={`${parramattaSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=parramatta-florists`}
              className="bg-white text-brand-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-3 text-lg min-h-[48px]"
            >
              Shop Parramatta Florists Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/blog/birthday-flowers-sydney" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Birthday Flowers Sydney: Best Options for Every Age
                  </h4>
                  <p className="leading-7 text-black">Find the perfect birthday flowers for any celebration</p>
                </div>
              </Link>
              <Link to="/blog/local-vs-corporate-florists" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Local vs Corporate Florists: Why Local Wins
                  </h4>
                  <p className="leading-7 text-black">Discover the benefits of choosing local Sydney florists</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 